import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Select from "react-select";

export default function Registration({  
  accent = "#115e32",
  pageTitle = "Registration | International Islamic University Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	
	const [step, setStep] = useState("form"); // form | otp | success
	const [form, setForm] = useState({
		category: "",
		name: "",
		gender: "",
		email: "",
		mobile: "",
		password: "",
		confirmPassword: "",
	});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	// OTP related
	const [otp, setOtp] = useState("");
	const [generatedOtp, setGeneratedOtp] = useState(null);
	const [otpSentTo, setOtpSentTo] = useState({ sms: false, email: false });
	const [attemptsLeft, setAttemptsLeft] = useState(3);
	const [categories, setCategories] = useState([]);
	
	const [error, setError] = useState(null);
	const [countdown, setCountdown] = useState(5);
  
	const categoryPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/job-category", payload);
			
			// Loop data (array)
			setCategories(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		categoryPost();		
	}, []);
	
	useEffect(() => {
		if (step === "success") {
		  const t = setInterval(() => setCountdown((c) => c - 1), 1000);
		  return () => clearInterval(t);
		}
	}, [step]);

  useEffect(() => {
    if (countdown <= 0 && step === "success") {
      window.location.href = "/login";
    }
  }, [countdown, step]);

  function validateForm() {
    const e = {};
    if (!form.category) e.category = "Please select a job category.";
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.gender) e.gender = "Please select gender.";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.mobile || !/^\+?\d{8,15}$/.test(form.mobile)) e.mobile = "Enter a valid mobile number.";
    if (!form.password || form.password.length < 6) e.password = "Password must be at least 6 characters.";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords do not match.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(code);

    try {
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			mobile: form.mobile,
			email: form.email,
			otp: code,
			purpose: "registration",
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const response = await api.post("/send-otp", payload);      

		setOtpSentTo({ sms: true, email: true });
		setStep("otp");
    } catch (err) {
      console.error(err);
      alert("Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtpAndRegister(e) {
    e?.preventDefault();
    if (!otp.trim()) return setErrors({ otp: "Enter the OTP." });
    if (attemptsLeft <= 0) return alert("No attempts left.");

    setLoading(true);

    try {
      
		const ok = (otp === generatedOtp);

		if (!ok) {
			setAttemptsLeft((a) => a - 1);
			setErrors({ otp: `Wrong OTP. Attempts left: ${attemptsLeft - 1}` });
			setLoading(false);
			return;
		}

		const regPayload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			category: form.category,
			name: form.name,
			gender: form.gender,
			email: form.email,
			mobile: form.mobile,
			cpassword: form.password,
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const regRes = await api.post("/registration", regPayload);
		
		if (regRes?.responseCode === 90) {
		  throw new Error(`Registration failed: ${regRes?.responseMsg || "Unknown error"}`);
		}
		
		setStep("success");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  const Input = ({ label, children, error }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
	const filteredCategories = categories.filter((cat) => {
		const name = cat?.occupation || cat?.occupation || cat?.occupation || "";
		return name.toLowerCase().includes(searchTerm.toLowerCase());
	});
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white p-6">
      <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="hidden md:flex flex-col justify-center items-center bg-white rounded-2xl shadow-lg p-8 overflow-hidden relative"
        >
          <svg className="absolute -top-10 -left-10 w-72 opacity-30" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="g1" x1="0" x2="1">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#a78bfa" />
              </linearGradient>
            </defs>
            <motion.g animate={{ rotate: 6 }} transition={{ duration: 6, yoyo: Infinity }}>
              <motion.circle cx="200" cy="100" r="140" fill="url(#g1)" opacity="0.25" />
            </motion.g>
            <motion.path d="M0 300 C150 200 450 400 600 300 L600 600 L0 600 Z" fill="#eef2ff" />
          </svg>

          <div className="relative z-10 text-center">
            <h2 className="text-2xl font-bold mb-2">Join our team</h2>
            <p className="text-sm text-slate-600 mb-6">Quick registration — verify with OTP and start applying.</p>

            <div className="w-48 h-48 mx-auto rounded-2xl bg-gradient-to-tr from-sky-200 to-purple-200 shadow-inner flex items-center justify-center">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="opacity-80">
                <path d="M12 2l4 4-4 4-4-4 4-4z" />
                <path d="M12 10v10" />
              </svg>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ x: 30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          {step === "form" && (
            <form onSubmit={handleSubmit}>
              <h3 className="text-xl font-semibold mb-4">Register</h3>
				<div>
					<label className="text-sm font-medium"> Select Job Category *</label>              
					<Select 
						name="category"
						className="text-sm"
						options={categories.map((cat) => ({
						  value: cat.id || cat.occupation,
						  label: cat.occupation,
						}))}
						placeholder="Search or select category..."
						value={
							form.category
							? {
								value: form.category,
								label:
								  categories.find(
									(c) => c.id === form.category || c.occupation === form.category
								  )?.occupation || form.category,
							  }
							: null
						}
						onChange={(selected) =>
						  setForm((s) => ({ ...s, category: selected ? selected.value : "" }))
						}
						isClearable
						isSearchable
					/>			
					{errors.category && <div className="text-red-500 text-sm">{errors.category}</div>}
				</div>
				
			  <div>
                <label className="text-sm font-medium"> Name *</label>
                <input name="name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="mt-2 w-full border rounded-md p-2" />
                {errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
              </div>
				
              

              <Input label="Select Gender" error={errors.gender}>
                <select value={form.gender} onChange={(e) => setForm((s) => ({ ...s, gender: e.target.value }))} className="w-full border rounded px-3 py-2">
                  <option value="">-- Gender --</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </Input>
			  <div>
                <label className="text-sm font-medium"> Email *</label>
                <input name="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} className="mt-2 w-full border rounded-md p-2" />
                {errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
              </div>
              <div>
                <label className="text-sm font-medium"> Mobile Number *</label>
                <input name="mobile" value={form.mobile} onChange={(e) => setForm((s) => ({ ...s, mobile: e.target.value }))} className="mt-2 w-full border rounded-md p-2" placeholder="01XXXXXXXXX"/>
                {errors.mobile && <div className="text-red-500 text-sm">{errors.mobile}</div>}
              </div>
			  <div>
                <label className="text-sm font-medium"> Password *</label>
                <input type="password" name="password" value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} className="mt-2 w-full border rounded-md p-2" />
                {errors.password && <div className="text-red-500 text-sm">{errors.password}</div>}
              </div>
              <div>
                <label className="text-sm font-medium">Confirm Password *</label>
                <input type="password" name="confirmPassword" value={form.confirmPassword} onChange={(e) => setForm((s) => ({ ...s, confirmPassword: e.target.value }))} className="mt-2 w-full border rounded-md p-2" />
                {errors.confirmPassword && <div className="text-red-500 text-sm">{errors.confirmPassword}</div>}
              </div>

              <div className="flex items-center justify-between mt-6">
                <button type="submit" disabled={loading} className="px-5 py-2 rounded bg-gradient-to-r from-sky-500 to-purple-500 text-white font-medium shadow hover:opacity-95 disabled:opacity-60">
                  {loading ? "Sending OTP..." : "Submit & Send OTP"}
                </button>
                <a href="/login" className="text-sm text-slate-500 hover:underline">Already registered? Login</a>
              </div>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtpAndRegister}>
              <h3 className="text-xl font-semibold mb-4">Enter OTP</h3>
              <p className="text-sm text-slate-600 mb-4">We sent a 6-digit code to your mobile and email.</p>
			  <div>
                <label className="text-sm font-medium"> OTP Code *</label>
                <input name="otp" value={otp} onChange={(e) => setOtp(e.target.value)} className="mt-2 w-full border rounded-md p-2" />
                {errors.otp && <div className="text-red-500 text-sm">{errors.otp}</div>}
              </div>             

              <div className="flex items-center justify-between mt-6">
                <button type="submit" disabled={loading} className="px-5 py-2 rounded bg-gradient-to-r from-emerald-500 to-sky-500 text-white font-medium shadow disabled:opacity-60">
                  {loading ? "Verifying..." : "Verify & Register"}
                </button>
                <button type="button" onClick={() => handleSubmit()} className="text-sm text-slate-600 hover:underline">Resend OTP</button>
              </div>
              <p className="text-xs text-slate-500 mt-3">Attempts left: {attemptsLeft}</p>
            </form>
          )}

          {step === "success" && (
            <div className="text-center py-8">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }} className="inline-block bg-emerald-50 p-6 rounded-2xl shadow">
                <h3 className="text-xl font-semibold mb-2">Registration Successful!</h3>
                <p className="text-sm text-slate-600">Redirecting to login in <span className="font-mono">{countdown}</span> seconds.</p>
                <div className="mt-4">
                  <a href="/login" className="px-4 py-2 rounded bg-white border">Go to Login Now</a>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
